import { ApiError, queryKeys } from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  humanizeStatus,
  Input,
  REPAIR_STATUS_TONE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Smartphone } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { RepairLabourCard } from '../components/repair-labour.js'
import { RepairPartsCard } from '../components/repair-parts.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface RepairDetail {
  id: string
  ticketNumber: string
  status: string
  customerId: string
  customerName: string | null
  customerPhone: string | null
  deviceDescription: string
  issue: string
  imei: string | null
  dropOffDate: string | null
  promisedDate: string | null
  workDate: string | null
  assignedStaffId: string | null
  assignedStaffName: string | null
  orderId: string | null
  orderNumber: string | null
  quotedCents: number | null
  createdAt: string
  updatedAt: string
}

interface RepairEvent {
  id: string
  fromStatus: string | null
  toStatus: string
  actorType: string
  actorLabel: string | null
  note: string | null
  createdAt: string
}

interface StaffRow {
  id: string
  name: string
  isActive: boolean
}

const ACTION_LABEL: Record<string, string> = {
  intake: 'Check in device',
  diagnosing: 'Start diagnosis',
  awaiting_parts: 'Waiting on parts',
  in_repair: 'Start repair',
  ready_for_pickup: 'Ready for pickup',
  completed: 'Mark collected',
  cancelled: 'Cancel',
}

const UNASSIGNED = '__unassigned__'

/**
 * One repair ticket: its two dates, who is booked on it, the parts used on
 * it, and its status history. Same shape as the stock request detail page — legal moves come
 * from the server, not a list kept here.
 */
export function RepairDetailPage() {
  const { ticketId } = useParams({ from: '/authed/repairs/$ticketId' })
  const { can } = useAuth()
  const [moving, setMoving] = useState<string | null>(null)
  const [editingDetails, setEditingDetails] = useState(false)

  const { data: ticket, isLoading } = useQuery({
    queryKey: queryKeys.repairs.detail(ticketId),
    queryFn: () => api.get<RepairDetail>(`/repairs/${ticketId}`),
  })

  const { data: transitions } = useQuery({
    queryKey: queryKeys.repairs.transitions(ticketId),
    queryFn: () => api.get<{ from: string; to: string[] }>(`/repairs/${ticketId}/transitions`),
    enabled: Boolean(ticket),
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.repairs.events(ticketId),
    queryFn: () => api.get<RepairEvent[]>(`/repairs/${ticketId}/events`),
    enabled: Boolean(ticket),
  })

  if (isLoading || !ticket) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  const moves = can('repairs:write') ? (transitions?.to ?? []) : []

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/repairs"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All repairs
        </Link>
      </div>

      <PageHeader
        title={ticket.ticketNumber}
        description={`${ticket.customerName ?? 'Customer'}${
          ticket.customerPhone ? ` · ${ticket.customerPhone}` : ''
        } · opened ${new Date(ticket.createdAt).toLocaleString('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="repair" itemId={ticketId} />
            <Badge tone={REPAIR_STATUS_TONE[ticket.status] ?? 'neutral'}>
              {humanizeStatus(ticket.status)}
            </Badge>
            {moves.map((to) => (
              <Button
                key={to}
                size="sm"
                variant={to === 'cancelled' ? 'ghost' : 'primary'}
                onClick={() => setMoving(to)}
              >
                {ACTION_LABEL[to] ?? humanizeStatus(to)}
              </Button>
            ))}
          </div>
        }
      />

      {moves.length === 0 && transitions?.to.length === 0 ? (
        <Alert tone="info" className="mb-4">
          This ticket is closed. Nothing further can be done to it.
        </Alert>
      ) : null}

      {ticket.orderNumber ? (
        <Alert tone="info" className="mb-4">
          Opened from{' '}
          {ticket.orderId ? (
            <Link
              to="/orders/$orderId"
              params={{ orderId: ticket.orderId }}
              className="font-medium underline"
            >
              {ticket.orderNumber}
            </Link>
          ) : (
            ticket.orderNumber
          )}
          .
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="size-4" aria-hidden="true" /> The device
              </CardTitle>
              {can('repairs:write') ? (
                <Button size="sm" variant="secondary" onClick={() => setEditingDetails(true)}>
                  <Pencil className="size-3.5" aria-hidden="true" /> Edit
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Device</p>
                <p>{ticket.deviceDescription}</p>
              </div>
              {ticket.imei ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">IMEI</p>
                  <p className="font-identifier">{ticket.imei}</p>
                </div>
              ) : null}
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Issue</p>
                <p>{ticket.issue}</p>
              </div>
              {ticket.quotedCents !== null ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Quote</p>
                  <p>${(ticket.quotedCents / 100).toFixed(2)}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <RepairPartsCard ticketId={ticket.id} cancelled={ticket.status === 'cancelled'} />

          <RepairLabourCard
            ticketId={ticket.id}
            ticketNumber={ticket.ticketNumber}
            customerId={ticket.customerId}
            assignedStaffId={ticket.assignedStaffId}
            cancelled={ticket.status === 'cancelled'}
          />

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <SchedulingCard ticket={ticket} />
        </div>
      </div>

      <TransitionDialog ticket={ticket} to={moving} onClose={() => setMoving(null)} />
      <EditDetailsDialog
        ticket={ticket}
        open={editingDetails}
        onClose={() => setEditingDetails(false)}
      />
    </Page>
  )
}

/**
 * The page's main job: the two dates and who is booked. Inline rather than a
 * dialog — this is what someone opens the ticket to change, not an
 * occasional edit tucked behind a button.
 */
function SchedulingCard({ ticket }: { ticket: RepairDetail }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('repairs:write')
  const canSeeStaff = can('staff:read')

  const [dropOffDate, setDropOffDate] = useState(ticket.dropOffDate ?? '')
  const [promisedDate, setPromisedDate] = useState(ticket.promisedDate ?? '')
  const [workDate, setWorkDate] = useState(ticket.workDate ?? '')
  const [assignedStaffId, setAssignedStaffId] = useState(ticket.assignedStaffId ?? UNASSIGNED)

  // The pickup date is a promise made once the device is actually here —
  // there is nothing to promise before that.
  const promisedDateLocked = ticket.status === 'awaiting_dropoff'

  useEffect(() => {
    setDropOffDate(ticket.dropOffDate ?? '')
    setPromisedDate(ticket.promisedDate ?? '')
    setWorkDate(ticket.workDate ?? '')
    setAssignedStaffId(ticket.assignedStaffId ?? UNASSIGNED)
  }, [ticket.dropOffDate, ticket.promisedDate, ticket.workDate, ticket.assignedStaffId])

  const { data: staff } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
    enabled: canSeeStaff,
  })
  const activeStaff = (staff ?? []).filter((s) => s.isActive)

  const dirty =
    dropOffDate !== (ticket.dropOffDate ?? '') ||
    (!promisedDateLocked && promisedDate !== (ticket.promisedDate ?? '')) ||
    workDate !== (ticket.workDate ?? '') ||
    assignedStaffId !== (ticket.assignedStaffId ?? UNASSIGNED)

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/repairs/${ticket.id}`, {
        dropOffDate: dropOffDate || null,
        // Left out entirely while locked, not sent as unchanged — the
        // server refuses to set this before the device is checked in, and an
        // unrelated save (work date, technician) should not trip that.
        ...(promisedDateLocked ? {} : { promisedDate: promisedDate || null }),
        workDate: workDate || null,
        assignedStaffId: assignedStaffId === UNASSIGNED ? null : assignedStaffId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all() })
      toast.success('Scheduling updated')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scheduling</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Drop-off date" hint="When the customer said they would bring it in.">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={dropOffDate}
              onChange={(e) => setDropOffDate(e.target.value)}
              disabled={!canWrite}
            />
          )}
        </Field>

        <Field
          label="Pickup date"
          hint={
            promisedDateLocked
              ? 'Available once the device is checked in.'
              : 'When we told the customer it will be ready to collect.'
          }
        >
          {(props) => (
            <Input
              {...props}
              type="date"
              value={promisedDate}
              onChange={(e) => setPromisedDate(e.target.value)}
              disabled={!canWrite || promisedDateLocked}
            />
          )}
        </Field>

        <Field label="Work date" hint="When a technician is booked to work on it.">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              disabled={!canWrite}
            />
          )}
        </Field>

        {canSeeStaff ? (
          <Field label="Technician">
            {(props) => (
              <Select
                value={assignedStaffId}
                onValueChange={setAssignedStaffId}
                disabled={!canWrite}
              >
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {activeStaff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        ) : null}

        {canWrite ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function TransitionDialog({
  ticket,
  to,
  onClose,
}: {
  ticket: RepairDetail
  to: string | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  const move = useMutation({
    mutationFn: () =>
      api.post(`/repairs/${ticket.id}/transition`, { to, note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all() })
      toast.success(`${ticket.ticketNumber} is now ${humanizeStatus(to ?? '').toLowerCase()}`)
      setNote('')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not update the ticket',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const isDestructive = to === 'cancelled'

  return (
    <Dialog open={to !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''} — {ticket.ticketNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isDestructive ? (
            <Alert tone="warning">This closes the ticket. It stays on record either way.</Alert>
          ) : null}
          <Field label="Note" hint="Kept on the ticket history.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={() => move.mutate()}
            disabled={move.isPending}
          >
            {move.isPending ? 'Working…' : to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditDetailsDialog({
  ticket,
  open,
  onClose,
}: {
  ticket: RepairDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [deviceDescription, setDeviceDescription] = useState(ticket.deviceDescription)
  const [issue, setIssue] = useState(ticket.issue)
  const [quoted, setQuoted] = useState(
    ticket.quotedCents !== null ? (ticket.quotedCents / 100).toFixed(2) : '',
  )

  useEffect(() => {
    if (!open) return
    setDeviceDescription(ticket.deviceDescription)
    setIssue(ticket.issue)
    setQuoted(ticket.quotedCents !== null ? (ticket.quotedCents / 100).toFixed(2) : '')
  }, [open, ticket.deviceDescription, ticket.issue, ticket.quotedCents])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/repairs/${ticket.id}`, {
        deviceDescription: deviceDescription.trim(),
        issue: issue.trim(),
        quotedCents: quoted.trim() ? Math.round(Number(quoted) * 100) : null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all() })
      toast.success('Ticket updated')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const valid = deviceDescription.trim().length > 0 && issue.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {ticket.ticketNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Device" required>
            {(props) => (
              <Input
                {...props}
                value={deviceDescription}
                onChange={(e) => setDeviceDescription(e.target.value)}
              />
            )}
          </Field>
          <Field label="Issue" required>
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Quote"
            hint="Starts at the price paid at checkout — adjust after inspection if needed."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={quoted}
                onChange={(e) => setQuoted(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
